import React, { useEffect, useState } from 'react';
import { Dialog } from "primereact/dialog";
import { formatDate } from "../../../utils/helper";
import { ageTypes } from "../../../utils/staticData";

const PatientUpdateModal = ({
    showPatientModal,
    setShowPatientModal,
    handlePatientSubmit,
    setPatientData,
    patientData, loading
}: any) => {
    const [age, setAge] = useState<any>('');
    const [ageType, setAgeType] = useState<string>('years');

    const handlePatientInputChange = (e: any) => {
        const { name, value } = e.target;
        setPatientData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const calculateDobFromAge = (ageValue: any, type: string) => {
        const numericAge = parseFloat(ageValue);
        if (isNaN(numericAge)) return;
        const dob = new Date();
        const whole = Math.floor(numericAge);
        const frac = numericAge - whole;
        if (type === 'years') {
            dob.setFullYear(dob.getFullYear() - whole);
            if (frac) dob.setMonth(dob.getMonth() - Math.round(frac * 12));
        } else if (type === 'months') {
            dob.setMonth(dob.getMonth() - whole);
            if (frac) dob.setDate(dob.getDate() - Math.round(frac * 30));
        } else if (type === 'days') {
            dob.setDate(dob.getDate() - Math.round(numericAge));
        }
        setPatientData((prev: any) => ({ ...prev, dob: dob.toISOString().split('T')[0] }));
    };

    const calculateAgeFromDob = (dob: any) => {
        const birthDate = new Date(dob);
        const diffMs = new Date().getTime() - birthDate.getTime();
        if (isNaN(diffMs) || diffMs < 0) { setAge(''); return; }
        const days = diffMs / (1000 * 60 * 60 * 24);
        if (days >= 365) {
            setAge(Math.round((days / 365.25) * 10) / 10);
            setAgeType('years');
        } else if (days >= 30) {
            setAge(Math.round((days / 30.4375) * 10) / 10);
            setAgeType('months');
        } else {
            setAge(Math.round(days));
            setAgeType('days');
        }
    };

    const handleAgeChange = (e: any) => {
        const value = e.target.value;
        setAge(value);
        calculateDobFromAge(value, ageType);
    };

    const handleAgeTypeChange = (e: any) => {
        const value = e.target.value;
        setAgeType(value);
        if (age) calculateDobFromAge(age, value);
    };

    const handleDobChange = (e: any) => {
        const value = e.target.value;
        setPatientData((prev: any) => ({ ...prev, dob: value }));
        calculateAgeFromDob(value);
    };

    useEffect(() => {
        if (!showPatientModal) return;
        if (patientData?.dob) {
            calculateAgeFromDob(patientData.dob);
        } else {
            setAge('');
            setAgeType('years');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPatientModal]);

    return (
        <div>
            <Dialog
                visible={showPatientModal}
                style={{ width: "40rem" }}
                headerClassName="text-800 font-semibold text-xl"
                header={"Update Patient"}
                breakpoints={{ "960px": "75vw", "640px": "90vw" }}
                onHide={() => {
                    setShowPatientModal(false)
                    setPatientData(null)
                }}
            >
                <form onSubmit={handlePatientSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700">Name</label>
                        <input
                            type="text"
                            name="name"
                            defaultValue={patientData?.name}
                            onChange={handlePatientInputChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700">Phone</label>
                        <input
                            type="text"
                            name="phone"
                            defaultValue={patientData?.phone}
                            onChange={handlePatientInputChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none"
                        />
                    </div>

                    <div className="mb-4 grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-gray-700">Age <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                inputMode="decimal"
                                pattern="[0-9]*\.?[0-9]*"
                                name="age"
                                value={age}
                                onChange={handleAgeChange}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700">Age Type</label>
                            <select
                                name="ageType"
                                value={ageType}
                                onChange={handleAgeTypeChange}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none bg-white"
                            >
                                {ageTypes?.map((t: string) => (
                                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700">Date of Birth <span className="text-red-500">*</span></label>
                        <input
                            type="date"
                            name="dob"
                            value={formatDate(patientData?.dob)}
                            onChange={handleDobChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none"
                            required
                        />
                    </div>


                    <div className="mb-4">
                        <label className="block text-gray-700">Gender</label>
                        <input
                            type="text"
                            name="gender"
                            defaultValue={patientData?.gender}
                            onChange={handlePatientInputChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none"
                        />
                    </div>


                    <div className="flex justify-start">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-primary text-white rounded-md"
                            disabled={loading}
                        >
                            Update
                        </button>
                    </div>
                </form>
            </Dialog>
        </div>
    );
};

export default PatientUpdateModal;
